import { Box, Step, StepLabel, Stepper, StepConnector } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useState, useEffect } from "react";
import classes from "./MedalInfo.module.scss";
import { ReactComponent as IconCheckSquare } from "../../../../assets/images/icons/check_square_regular.svg";
import { ReactComponent as IconSquare } from "../../../../assets/images/icons/square_regular.svg";
import { ReactComponent as ImageGrayStar } from "../../../../assets/images/steps/gray_star.svg";
import { ReactComponent as ImageColorStar } from "../../../../assets/images/steps/color_star.svg";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/system";

export const useMedalInfoLogic = ({ medal }) => {
  const { t } = useTranslation();
  const [completedConditions, setCompletedConditions] = useState(0);
  const [isMedalComplete, setIsMedalComplete] = useState(false);

  // Atualiza o número de condições completadas quando as condições mudam
  useEffect(() => {
    if (medal?.conditions) {
      const completed = medal?.conditions.filter(
        (condition) => condition.is_complete
      ).length;
      setCompletedConditions(completed);
    }
  }, [medal?.conditions]);

  // Atualiza o estado de medal_complete quando medal muda
  useEffect(() => {
    if (medal) {
      setIsMedalComplete(medal.medal_complete);
    }
  }, [medal]);

  //Função do conector do step
  const CustomStepConnector = styled(StepConnector)(
    ({ theme, completedConditions, medal }) => ({
      margin: "0 -12px",
      "& .MuiStepConnector-line": {
        borderColor:
          medal?.medal_complete || completedConditions > 0
            ? "#FFCA55"
            : "#d7d7d7",
        borderWidth: 6,
        opacity: 1,
        transition: "all 0.3s ease-in-out",
        height: "2px",
      },
    })
  );

  // Componente para renderizar os ícones e a lógica da estrela do step
  const StepIcon = ({
    index,
    max_level,
    completedConditions,
    medal,
    conditions,
  }) => {
    let imageStar;

    // Renderiza estrela colorida ou cinza dependendo do estado
    if (medal?.medal_complete || completedConditions > index) {
      imageStar = (
        <div>
          <ImageColorStar />
        </div>
      );
    } else {
      imageStar = (
        <div>
          <ImageGrayStar />
        </div>
      );
    }

    const isLevelCompleted =
      completedConditions > index || medal?.medal_complete;

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {index >= max_level - 1 ? (
          <Box
            sx={{
              width: "72px",
              height: "72px",
              position: "relative",
              top: "-20px",
            }}
          >
            {imageStar}{" "}
          </Box>
        ) : (
          <Box
            sx={{
              width: "24px",
              height: "24px",
              backgroundColor: isLevelCompleted ? "#FFCA55" : "#D7D7D7",
              borderRadius: "50%",
            }}
          />
        )}
      </Box>
    );
  };

  // Exibe as condições da medalha e indica se estão completadas
  const MedalConditions = ({ conditions, max_level }) => {
    return (
      <Grid container spacing={2} alignItems="center">
        {/* Grid item para a imagem */}
        <Grid item size={{ xs: 12 }}>
          <Box>
            {/* Percorre os níveis de 1 até max_level */}
            {Array.from({ length: max_level }).map((_, index) => {
              // Verifica se existe uma condição para o nível atual e se ela está completa
              const condition = Array.isArray(conditions)
                ? conditions.find(
                    (cond) => parseInt(cond.condition) === index + 1
                  )
                : null;
              // Determina se a condição está completa, caso contrário, considera como não completada
              const isCompleted = condition ? condition.is_complete : false;

              return (
                <Box className={classes.ItsDoneWrap} key={index}>
                  {/* Exibe o ícone de check (completado) ou square (não completado) */}
                  <span>
                    {isCompleted ? <IconCheckSquare /> : <IconSquare />}
                  </span>
                  {/* Exibe o nível da condição e o score correspondente */}
                  Nível: {index + 1} - Pontuação:{" "}
                  {condition ? condition.score : "Não disponível"}
                </Box>
              );
            })}
          </Box>
        </Grid>
      </Grid>
    );
  };

  // Função para estilizar a imagem dependendo do estado de completude
  const getImageStyles = (conditions) => {
    const conditionsArray = Array.isArray(conditions) ? conditions : []; // Garante que seja um array

    const isCompleted =
      isMedalComplete ||
      conditionsArray.every((condition) => condition.is_complete);

    return {
      width: "100%",
      maxHeight: "300px",
      objectFit: "cover",
      filter: !isCompleted ? "grayscale(100%) blur(4px)" : "none",
      transform: !isCompleted ? "scale(1.05)" : "none",
      transition: "all 0.3s ease",
    };
  };

  // Função para renderizar as estrelas (steps) no processo
  function getStars(max_level, conditions) {
    return (
      <Box className={classes.WrapSteps}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: "300px",
            margin: "auto",
          }}
        >
          <Stepper
            activeStep={completedConditions}
            alternativeLabel
            connector={
              <CustomStepConnector
                completedConditions={completedConditions}
                medal={medal}
              />
            }
            sx={{
              padding: "0",
              gap: "0px",
              flexGrow: 1,
              marginTop: "20px",
            }}
          >
            {Array.from({ length: max_level }).map((_, index) => (
              <Step key={index}>
                <StepLabel
                  StepIconComponent={() => (
                    <StepIcon
                      index={index}
                      max_level={max_level}
                      completedConditions={completedConditions}
                      medal={medal}
                      conditions={conditions}
                    />
                  )}
                />
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>
    );
  }

  // Retornando os valores como objeto
  return {
    completedConditions,
    isMedalComplete,
    CustomStepConnector,
    StepIcon,
    MedalConditions,
    getImageStyles,
    getStars,
  };
};
